import { ValidationResult } from "@src/core/engine/validationResults/buildValidationResult";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  parseValidationResult,
  TranslationHandler,
} from "./parseValidationResult";

export type UseValidationErrorParserOptions = {
  constraintsT?: Partial<TranslationHandler>;
  fieldsT?: Partial<TranslationHandler>;
  ignoreConversion?: boolean;
  maxDecimals?:
    | {
        max?: number;
        min?: number;
        value?: number;
      }
    | number;
};

export function useParseValidationResult(
  options?: UseValidationErrorParserOptions,
) {
  const { i18n, t } = useTranslation("errors");
  const { convertToUserUnit, getUnitSymbol } = useUnitConversion();
  const constraintsPrefix = options?.constraintsT?.prefix ?? "validation";
  const constraintsTFn = options?.constraintsT?.t ?? t;
  const fieldsPrefix = options?.fieldsT?.prefix ?? "validation.fields";
  const fieldsTFn = options?.fieldsT?.t ?? t;
  const maxDecimals = options?.maxDecimals ?? 3;
  const ignoreConversion = options?.ignoreConversion;
  const { language } = i18n;

  return useCallback(
    (
      validationResult: ValidationResult,
      overrides?: UseValidationErrorParserOptions,
    ) => {
      return parseValidationResult(language, {
        constraintsT: {
          prefix: overrides?.constraintsT?.prefix ?? constraintsPrefix,
          t: overrides?.constraintsT?.t ?? constraintsTFn,
        },
        conversionFn: ignoreConversion ? undefined : convertToUserUnit,
        fieldsT: {
          prefix: overrides?.fieldsT?.prefix ?? fieldsPrefix,
          t: overrides?.fieldsT?.t ?? fieldsTFn,
        },
        getSymbol: getUnitSymbol,
        maxDecimals: overrides?.maxDecimals ?? maxDecimals,
        validationResult,
      });
    },
    [
      constraintsPrefix,
      constraintsTFn,
      convertToUserUnit,
      fieldsPrefix,
      fieldsTFn,
      getUnitSymbol,
      ignoreConversion,
      language,
      maxDecimals,
    ],
  );
}
